import { c as _c } from "react/compiler-runtime";
import Head from 'next/head';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import Button from '@helios/Button';
import ButtonGroup from '@helios/ButtonGroup';
import ButtonLink from '@helios/ButtonLink';
import Card from '@helios/Card';
import Container from '@helios/Container';
import ContentBlock from '@helios/ContentBlock';
import Grid from '@helios/Grid';
import Media from '@helios/Media';
import PageHero from '@helios/PageHero';
import Section from '@helios/Section';
import SectionHeader from '@helios/SectionHeader';
import StickyScrollLayout from '@helios/StickyScrollLayout';
import TestimonialCarousel from '@helios/TestimonialCarousel';
import VideoModal from '@helios/VideoModal';
import { useState, useEffect } from 'react';
import classNames from 'classnames';
import Layout from '@/components/Layout';
import StructuredData from '@/components/StructuredData.js';
import CTA from '@/components/CTA.js';
import ApplyNow from '@/components/ApplyNow.js';
import useScroll from '@/hooks/useScroll.js';
import { allSites, hasHeaderTicker } from '@/utils/domains.js';
import { COST } from '@/utils/programCosts.js';
import { getExperiencePageSchema } from '@/utils/seo/siteSchemas.js';
import LeadFormLink from '@/components/LeadFormLink.js';
import { SiteFAQSection } from '@/components/SiteFAQAccordion.js';
import { COMMON_SITE_FAQS } from '@/utils/siteFaqs.js';
import { studentStorySlides } from '@/utils/studentStories.js';
import { LAPTOP_ELIGIBILITY } from '@/utils/laptopEligibility.js';
import Map from '@/public/images/map.svg';
import PlatFormSlug1 from '@/public/images/platform-slug-1.jpg';
import PlatFormSlug2 from '@/public/images/platform-slug-2.jpg';
import PlatFormSlug3 from '@/public/images/platform-slug-3.jpg';
import PlatFormSlug4 from '@/public/images/platform-slug-4.jpg';
import PlatFormSlug5 from '@/public/images/platform-slug-5.jpg';
import PlatFormSlug6 from '@/public/images/platform-slug-6.jpg';
import PlatFormSlug7 from '@/public/images/platform-slug-7.jpg';
import LightArrowDownLongIcon from '@/components/icons/light/arrow-down-long';
import { useSiteHref } from '@/utils/siteHref';
import heroContentStyles from '@/styles/PageHeroContent.module.scss';
import styles from '@/styles/ExperiencePage.module.scss';
const Globe = dynamic(() => import('@/components/Globe.js'), {
  ssr: false
});

// The rendered widths (Phase 5 PR 5.4). The map fills its card's copy column:
// the card is half the Container (default gutters, 40px apart) from 768px and
// the full column below, less its padding (20px a side below 768px, 40px to
// 991px, 80px from 992px).
const MAP_IMAGE_SIZES = '(max-width: 767px) calc(100vw - 80px), (max-width: 991px) calc(50vw - 132px), (max-width: 1199px) calc(50vw - 212px), (max-width: 1727px) calc(50vw - 244px), 620px';
// A chapter's image fills its Card (32px padding; 16px below 576px). Below
// 992px the StickyScrollLayout stacks in the default Container (20px gutters
// below 768px, 32px to 1199px); from 992px the content column is the
// Container less the 500px sidebar (variant="wide"), 1600px wide at most.
const CHAPTER_IMAGE_SIZES = '(max-width: 575px) calc(100vw - 72px), (max-width: 767px) calc(100vw - 104px), (max-width: 991px) calc(100vw - 128px), (max-width: 1199px) calc(100vw - 628px), (max-width: 1727px) calc(100vw - 692px), 1036px';

// The Campus video (Phase 6, Alex 2026-10-06 p01: "rebrand — but use the
// redesign's video"): the redesign experience hero's clip and poster (the
// retired campus-redesign-v1 experiment (removed in Phase 7)), in place
// under public/videos. Muted and looping, as decoration; it holds
// on the poster for a visitor who prefers reduced motion (Media). "Watch full
// video" opens the full film (the redesign's `fullVideoId`) in VideoModal,
// which plays a YouTube watch URL on the privacy-enhanced (nocookie) embed.
const HERO_VIDEO = {
  src: '/videos/experience-hero-ashley-v1.mp4',
  poster: '/videos/experience-hero-ashley-v1-poster.jpg',
  fullVideo: 'https://www.youtube.com/watch?v=2meHfTMbK0Y',
  label: 'Campus student experience'
};

// The student experience (Phase 6 p03–p10, the redesign's chapters on Helios
// StickyScrollLayout): the redesign's titles, words and images, in its order,
// with no chapter numbers (Alex's rule 1: no "CH.01", no "01 / 07"). The tech
// pack's laptop line is the shared eligibility sentence
// (utils/laptopEligibility.js, the redesign's `redesignLaptopEligibilitySummary`).
const CHAPTERS = [{
  id: 'live-classes',
  title: 'Live classes',
  image: PlatFormSlug1,
  body: 'Classes in the Campus Scholars programs are live so that you can directly interact with professors and peers. We keep things engaging by holding lively discussions and debates, breakout sessions, forums, and more.'
}, {
  id: 'tutoring',
  title: 'Tutoring',
  image: PlatFormSlug2,
  body: 'Students have unlimited access to 1:1 tutoring at no additional cost. Our tutoring services are accessible 24/7 so that you can get the academic support you need, wherever and whenever.'
}, {
  id: 'office-hours',
  title: 'Office hours',
  image: PlatFormSlug3,
  body: 'Benefit from unprecedented access to professors from world-class universities and accomplished TAs through live 1:1 office hours. Faculty are available every week so you can make connections, build rapport, and deepen your knowledge.'
}, {
  id: 'success-coach',
  title: 'Success coach',
  image: PlatFormSlug4,
  body: "Before classes begin, you'll be matched with a personal success coach who will serve as your confidant, cheerleader, and advisor all the way through graduation. They'll help you set and achieve your academic and professional goals during your time at Campus."
}, {
  id: 'tech-pack',
  title: 'Tech pack',
  image: PlatFormSlug5,
  body: `${LAPTOP_ELIGIBILITY} Campus also provides e-textbooks and digital course materials at no additional cost.`
}, {
  id: 'social-network',
  title: 'Social network',
  image: PlatFormSlug6,
  body: "Through Campus, you'll meet like-minded individuals and create deep, meaningful relationships with your peers. Find lifelong friends who share similar interests through student clubs and curated social events."
}, {
  id: 'events',
  title: 'Events',
  image: PlatFormSlug7,
  body: 'Explore your career options through exclusive Campus-hosted events featuring industry experts from companies like Google, TikTok, and Spotify. Also enjoy game nights, trivia nights, and themed social events.'
}];
export const getStaticPaths = async () => {
  return {
    paths: allSites.map(site => ({
      params: {
        site
      }
    })),
    fallback: false
  };
};
export async function getStaticProps({
  params
}) {
  return {
    props: {
      site: params.site
    }
  };
}
function Experience(t0) {
  const $ = _c(54);
  const {
    site
  } = t0;
  const sacramentoPrograms = useSiteHref("sacramento", "/programs");
  const [lightNav, setLightNav] = useState(false);
  const [heroVideoOpen, setHeroVideoOpen] = useState(false);
  const {
    scrollTo
  } = useScroll();
  const isSacramento = site === "sacramento";
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const handleScroll = () => {
        const el = document.getElementById("experience-trigger");
        if (!el) {
          return;
        }
        if (window.scrollY >= el.offsetTop - 210) {
          setLightNav(true);
        } else {
          setLightNav(false);
        }
      };
      window.addEventListener("scroll", handleScroll);
      return () => {
        window.removeEventListener("scroll", handleScroll);
      };
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Head><title>{"The Campus Online Experience \u2014 Live Classes & Real Support"}</title><link rel="canonical" href="https://campus.edu/experience" /><meta httpEquiv="X-UA-Compatible" content="IE=Edge" /><meta name="description" content="Experience online college that's anything but remote. Join live classes, connect with professors and peers, and get personalized support at Campus." /><meta name="viewport" content="width=device-width, initial-scale=1" /></Head>;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== isSacramento) {
    t4 = !isSacramento && <StructuredData id="experience-page-schema" data={getExperiencePageSchema()} />;
    $[3] = isSacramento;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <PageHero.Content placement="split" tone="inverse"><h1 className={classNames(heroContentStyles.title, heroContentStyles["title--feature"], heroContentStyles["title--inverse"])}>A community for ambitious students to start college.</h1><ButtonLink href="/admissions-application" arrow={true}>Apply now</ButtonLink></PageHero.Content>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== site) {
    t6 = classNames(styles["hero-video"], {
      [styles["hero-video--under-header"]]: hasHeaderTicker(site)
    });
    $[6] = site;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Media kind="video" src={HERO_VIDEO.src} poster={HERO_VIDEO.poster} autoPlay={true} loop={true} ratio="square" radius="none" onRequestModal={() => setHeroVideoOpen(true)} modalLabel="Watch full video" />;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t6) {
    t8 = <PageHero surface="dark"><PageHero.Container width="full"><PageHero.Split>{t5}<PageHero.Media placement="split"><div className={t6}>{t7}</div></PageHero.Media></PageHero.Split></PageHero.Container></PageHero>;
    $[9] = t6;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== heroVideoOpen) {
    t9 = heroVideoOpen && <VideoModal open={true} onClose={() => setHeroVideoOpen(false)} label={HERO_VIDEO.label} src={HERO_VIDEO.fullVideo} />;
    $[11] = heroVideoOpen;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <SectionHeader title="Learn from anywhere." titleScale="lg" layout="stack" spacing="spacious" />;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      base: 1,
      md: 2
    };
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = classNames(styles["type-card"], styles["type-card--globe"]);
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  let t14;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <h3 className={styles["type-title"]}>Campus Scholars</h3>;
    t14 = <p className={styles["type-body"]}>Our programs are specifically targeted toward students from anywhere who want to learn from the best. If you're looking for the flexibility of a fully online program with all the benefits of a college community, the digital experience is for you. Take live, online classes with world-class professors from wherever you are.</p>;
    $[16] = t13;
    $[17] = t14;
  } else {
    t13 = $[16];
    t14 = $[17];
  }
  let t15;
  if ($[18] !== scrollTo) {
    t15 = () => {
      scrollTo(document.getElementById("student-experience"));
    };
    $[18] = scrollTo;
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  let t16;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <LightArrowDownLongIcon />;
    $[20] = t16;
  } else {
    t16 = $[20];
  }
  let t17;
  if ($[21] !== t15) {
    t17 = <div className={styles["type-copy"]}>{t13}{t14}<p className={styles["type-action"]}><Button className={styles["type-action-button"]} variant="text" tone="neutral" size="lg" onClick={t15} iconRight={t16}>Preview the Campus Scholars experience</Button></p></div>;
    $[21] = t15;
    $[22] = t17;
  } else {
    t17 = $[22];
  }
  let t18;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <div className={styles["globe-wrap"]}><div className={styles.globe} suppressHydrationWarning={true}><Globe /></div></div>;
    $[23] = t18;
  } else {
    t18 = $[23];
  }
  let t19;
  if ($[24] !== t17) {
    t19 = <Card className={t12} fullHeight={true} surface="subtle">{t17}{t18}</Card>;
    $[24] = t17;
    $[25] = t19;
  } else {
    t19 = $[25];
  }
  let t20;
  let t21;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <h3 className={styles["type-title"]}>Campus Sacramento</h3>;
    t21 = <p className={styles["type-body"]}>Augment your learning with hands-on training and practice with some of our in-person programs like Medical Assisting, Phlebotomy, and Cosmetology and Barbering at Paul Mitchell the School and our Sacramento campus.</p>;
    $[26] = t20;
    $[27] = t21;
  } else {
    t20 = $[26];
    t21 = $[27];
  }
  let t22;
  if ($[28] !== sacramentoPrograms) {
    t22 = <div className={styles["type-copy"]}>{t20}{t21}<ButtonLink href={sacramentoPrograms} clientNavigation={false} variant="text" tone="neutral" size="lg" className={styles["type-link"]} arrow={true}>Learn more about our programs</ButtonLink></div>;
    $[28] = sacramentoPrograms;
    $[29] = t22;
  } else {
    t22 = $[29];
  }
  let t23;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <div className={styles.map}><Image alt="figure" className={styles["map-image"]} sizes={MAP_IMAGE_SIZES} src={Map} /></div>;
    $[30] = t23;
  } else {
    t23 = $[30];
  }
  let t24;
  if ($[31] !== t22) {
    t24 = <Card className={styles["type-card"]} fullHeight={true} surface="subtle">{t22}{t23}</Card>;
    $[31] = t22;
    $[32] = t24;
  } else {
    t24 = $[32];
  }
  let t25;
  if ($[33] !== t19 || $[34] !== t24) {
    t25 = <Section id="experience-trigger"><Container>{t10}<Grid columns={t11} gap="lg">{t19}{t24}</Grid></Container></Section>;
    $[33] = t19;
    $[34] = t24;
    $[35] = t25;
  } else {
    t25 = $[35];
  }
  let t26;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <Section id="student-experience"><StickyScrollLayout variant="wide" sidebarHeader={<SectionHeader title="Helping you reach your potential." titleScale="lg" layout="stack" spacing="none" />} steps={CHAPTERS.map(_temp)} /></Section>;
    $[36] = t26;
  } else {
    t26 = $[36];
  }
  let t27;
  let t28;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <h3 className={styles["callout-title"]}>You may be able to attend with no out-of-pocket tuition costs.</h3>;
    t28 = <p>Tuition in our online Campus Scholars programs is set at{" "}{COST.annualTuition} per academic year, which breaks down to{" "}{COST.perCreditRate} per credit hour. Many of our currently enrolled AABA students have been able to pay their tuition with no out-of-pocket costs and without taking on any debt, thanks to the Federal Pell Grant program.</p>;
    $[37] = t27;
    $[38] = t28;
  } else {
    t27 = $[37];
    t28 = $[38];
  }
  let t29;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <Section><Container width="panel" gutters="compact"><ContentBlock padding="xl" rhythm="callout" surface="dark">{t27}{t28}<ButtonGroup><LeadFormLink variant="solid">Get in contact with Financial Aid</LeadFormLink></ButtonGroup></ContentBlock></Container></Section>;
    $[39] = t29;
  } else {
    t29 = $[39];
  }
  let t30;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <Container><SectionHeader title="Students, in their own words." titleScale="lg" layout="stack" spacing="spacious" /></Container>;
    $[40] = t30;
  } else {
    t30 = $[40];
  }
  let t31;
  let t32;
  let t33;
  let t34;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <Section>{t30}<TestimonialCarousel slides={studentStorySlides()} /></Section>;
    t32 = <SiteFAQSection faqs={COMMON_SITE_FAQS} />;
    t33 = <CTA />;
    t34 = <ApplyNow />;
    $[41] = t31;
    $[42] = t32;
    $[43] = t33;
    $[44] = t34;
  } else {
    t31 = $[41];
    t32 = $[42];
    t33 = $[43];
    t34 = $[44];
  }
  let t35;
  if ($[45] !== t25 || $[46] !== t4 || $[47] !== t8 || $[48] !== t9) {
    t35 = <div id="wrapper">{t3}{t4}{t8}{t9}{t25}{t26}{t29}{t31}{t32}{t33}{t34}</div>;
    $[45] = t25;
    $[46] = t4;
    $[47] = t8;
    $[48] = t9;
    $[49] = t35;
  } else {
    t35 = $[49];
  }
  let t36;
  if ($[50] !== lightNav || $[51] !== site || $[52] !== t35) {
    t36 = <Layout site={site} lightNav={lightNav}>{t35}</Layout>;
    $[50] = lightNav;
    $[51] = site;
    $[52] = t35;
    $[53] = t36;
  } else {
    t36 = $[53];
  }
  return t36;
}
function _temp(chapter) {
  return {
    id: `student-experience-${chapter.id}`,
    label: chapter.title,
    content: <Card surface="subtle" padded="md" mobilePadded="sm"><Media kind="image" src={chapter.image} alt={chapter.title} ratio="landscape" sizes={CHAPTER_IMAGE_SIZES} /><div className={styles["chapter-copy"]}><h3>{chapter.title}</h3><p className={styles["chapter-body"]}>{chapter.body}</p></div></Card>
  };
}
export default Experience;